import React, { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsMutating } from '@tanstack/react-query';

import { Arrow, Communication, General } from 'icons/untitledui';
import { DropdownMenu } from 'shared/components/dropdown/DropdownMenu';
import * as Buttons from 'theme/components/Buttons.style';
import ConfirmDialog from 'shiftNotes/components/ConfirmDialog';
import EditableNoteContent from 'shiftNotes/components/EditableNoteContent';
import NoteCardBody from 'shiftNotes/components/NoteCardBody';
import { isEmergencyNote } from 'shiftNotes/helpers/isEmergencyNote';
import {
  EXTRACT_ENTITIES_MUTATION_KEY,
  GENERATE_INSTANT_REFLEX_MUTATION_KEY,
  useDeleteShiftNoteMutation,
  useExtractEntitiesMutation,
  useGenerateInstantReflexMutation,
  useShiftNote,
  useUnlinkShiftNoteFromEventMutation,
  useMarkShiftNoteFalseAlarmMutation,
  useUpdateShiftNoteMutation,
} from 'shared/hooks/api/shiftNotes';
import { useUser } from 'shared/hooks/api/user';
import { Profile } from 'teamProfiles/_types';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import { wasNoteCreatedThisSession } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import {
  buildComposeUrl,
  EMAIL_PROVIDERS,
  EmailProvider,
  PROVIDER_LABEL_KEYS,
} from 'shared/components/EmailComposeMenu/providers';

import * as Styled from 'shiftNotes/styles/NotesStack.style';
import type { ReflexStep, StackedNoteCardData } from 'shiftNotes/_types';

import NoteAttachments from './NoteAttachments';
import NoteCardAnswersSection from './NoteCardAnswersSection';
import NoteCardGuidanceSection from './NoteCardGuidanceSection';
import NoteCardLoadingSection from './NoteCardLoadingSection';
import NoteCardMissingInfoSection from './NoteCardMissingInfoSection';
import NoteTranslationToggle from './NoteTranslationToggle';
import ReflexFeedbackSection from '../ReflexFeedback/_index';

interface StackedNoteCardProps {
  data: StackedNoteCardData;
  isSpecialist?: boolean;
  profiles?: Profile[];
  canUnsyncFromEvent?: boolean;
  eventId?: string;
}

type MenuMode = 'main' | 'share';

const GUIDANCE_STEP_TYPES = new Set<ReflexStep['step_type']>([
  'compose_message',
  'prefill_form',
  'ask_for_photo',
  'text',
]);

function hasPendingMissingInfo(data: StackedNoteCardData): boolean {
  const step = data.instantReflex?.steps?.find(
    (s) => s.step_type === 'ask_for_details'
  );
  if (!step) return false;
  return (step.questions?.length ?? 0) > 0 && !step.completed_at;
}

function hasGuidance(data: StackedNoteCardData): boolean {
  return Boolean(
    data.instantReflex?.steps?.some((s) => GUIDANCE_STEP_TYPES.has(s.step_type))
  );
}

function hasAnswers(data: StackedNoteCardData): boolean {
  return Boolean(
    data.instantReflex?.steps?.some((s) => s.step_type === 'give_answer')
  );
}

export default function StackedNoteCard({
  data,
  isSpecialist = false,
  profiles = [],
  canUnsyncFromEvent = false,
  eventId,
}: StackedNoteCardProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const { t: tShared } = useTranslation('shared');
  const capture = usePosthogCapture();
  const {
    authorName,
    time,
    body,
    originalBody,
    instantReflex,
    attachments = [],
  } = data;
  const sourceBody = originalBody ?? body;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuMode, setMenuMode] = useState<MenuMode>('main');
  const [isEditing, setIsEditing] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [editedText, setEditedText] = useState(sourceBody);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmergencyPrivacyConfirm, setShowEmergencyPrivacyConfirm] =
    useState(false);
  const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false);
  const [showFalseAlarmConfirm, setShowFalseAlarmConfirm] = useState(false);

  const { data: fullNote } = useShiftNote(data.id, { enabled: false });
  const { data: currentUser } = useUser();
  const noteAuthorId = data.userId ?? fullNote?.user?.id;
  const isNoteOwner = Boolean(
    currentUser?.id && noteAuthorId && noteAuthorId === currentUser.id
  );
  const { mutate: updateShiftNote } = useUpdateShiftNoteMutation();
  const { mutate: deleteShiftNote } = useDeleteShiftNoteMutation();
  const { mutate: unsyncFromEvent } = useUnlinkShiftNoteFromEventMutation();
  const { mutate: markFalseAlarm } = useMarkShiftNoteFalseAlarmMutation();
  const { mutate: extractEntities } = useExtractEntitiesMutation();
  const { mutate: generateInstantReflex } = useGenerateInstantReflexMutation();

  const isExtracting = useIsMutating({
    mutationKey: EXTRACT_ENTITIES_MUTATION_KEY,
    predicate: (mutation) => mutation.state.variables === data.id,
  });
  const isReflexGenerating = useIsMutating({
    mutationKey: GENERATE_INSTANT_REFLEX_MUTATION_KEY,
    predicate: (mutation) => mutation.state.variables === data.id,
  });

  useEffect(() => {
    if (!isMenuOpen) {
      setMenuMode('main');
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isEditing) {
      setEditedText(sourceBody);
    }
  }, [sourceBody, isEditing]);

  useEffect(() => {
    if (!originalBody) {
      setShowOriginal(false);
    }
  }, [originalBody]);

  const displayedBody = showOriginal ? sourceBody : body;
  const entitiesMatchDisplayedBody = !originalBody || showOriginal;

  const showMissingInfo =
    !isEditing && Boolean(instantReflex) && hasPendingMissingInfo(data);
  const showAnswers =
    !isEditing &&
    Boolean(instantReflex) &&
    !showMissingInfo &&
    hasAnswers(data);
  const showGuidance =
    !isEditing &&
    Boolean(instantReflex) &&
    !showMissingInfo &&
    hasGuidance(data);
  const showLoading =
    !isEditing &&
    !showMissingInfo &&
    !showAnswers &&
    !showGuidance &&
    (isExtracting > 0 || isReflexGenerating > 0);
  const showEmptyReflex =
    !isEditing &&
    Boolean(instantReflex) &&
    !showMissingInfo &&
    !showAnswers &&
    !showGuidance &&
    !showLoading;
  const loadingPhase: 'structuring' | 'thinking' =
    isExtracting > 0 ? 'structuring' : 'thinking';

  const wasJustCreatedThisSession = wasNoteCreatedThisSession(data.id);

  const stopClickPropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(displayedBody);
    capture.noteCopied();
  };

  const handleShareToProvider = (provider: EmailProvider) => {
    const subject = fullNote?.email_subject ?? '';
    const emailBody = fullNote?.email_body ?? body;
    window.open(
      buildComposeUrl(provider, { subject, body: emailBody }),
      '_blank',
      'noopener,noreferrer'
    );
    capture.noteShared({ provider });
  };

  const handleDeleteConfirmed = () => {
    if (!data.id) return;
    deleteShiftNote(data.id);
    capture.noteDeleted();
    setShowDeleteConfirm(false);
  };

  const handleUnsyncFromEvent = () => {
    if (!data.id) return;
    const shiftNoteId = data.id;
    unsyncFromEvent(shiftNoteId, {
      onSuccess: () =>
        capture.noteUnsyncedFromEvent({
          shift_note_id: shiftNoteId,
          event_id: eventId,
          user_id: currentUser?.id,
        }),
    });
  };

  const handleFalseAlarmConfirmed = () => {
    setShowFalseAlarmConfirm(false);
    if (!data.id) return;
    markFalseAlarm(data.id);
    capture.noteMarkedFalseAlarm();
  };

  const isPrivate = fullNote?.is_private ?? false;
  const isCameraNote = (data.source ?? fullNote?.source) === 'camera';

  const applyPrivacyChange = (nextPrivate: boolean) => {
    if (!data.id) return;
    setIsTogglingPrivacy(true);
    updateShiftNote(
      {
        id: data.id,
        data: { is_private: nextPrivate },
      },
      {
        onSettled: () => {
          setIsTogglingPrivacy(false);
          setIsMenuOpen(false);
        },
      }
    );
    if (nextPrivate) {
      capture.noteMarkedPrivate();
    } else {
      capture.noteMarkedPublic();
    }
  };

  const handleTogglePrivacy = () => {
    if (!data.id) return;
    const nextPrivate = !isPrivate;
    if (nextPrivate && fullNote && isEmergencyNote(fullNote)) {
      capture.noteMarkPrivateEmergencyWarningShown();
      setIsMenuOpen(false);
      setShowEmergencyPrivacyConfirm(true);
      return;
    }
    applyPrivacyChange(nextPrivate);
  };

  const handleConfirmEmergencyPrivacy = () => {
    capture.noteMarkPrivateEmergencyWarningConfirmed();
    setShowEmergencyPrivacyConfirm(false);
    applyPrivacyChange(true);
  };

  const handleCancelEmergencyPrivacy = () => {
    capture.noteMarkPrivateEmergencyWarningCancelled();
    setShowEmergencyPrivacyConfirm(false);
  };

  const renderPrivacyMenuIcon = () => {
    if (isTogglingPrivacy) {
      return (
        <Styled.MenuItemLoadingIcon aria-hidden="true">
          <General.Loading02 />
        </Styled.MenuItemLoadingIcon>
      );
    }
    if (isPrivate) return <General.Eye size={20} />;
    return <General.EyeOff size={20} />;
  };

  const handleEnterEdit = () => {
    setEditedText(sourceBody);
    setIsEditing(true);
    capture.noteEditModeOpened();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText(sourceBody);
    capture.noteEditModeCancelled({ shift_note_id: data.id });
  };

  const handleSaveEdit = () => {
    const trimmed = editedText.trim();
    if (!trimmed || trimmed === sourceBody.trim()) {
      setIsEditing(false);
      return;
    }
    updateShiftNote(
      {
        id: data.id,
        data: { edited_text: trimmed },
        clearEntities: true,
      },
      {
        onSuccess: () => {
          extractEntities(data.id);
          generateInstantReflex(data.id);
        },
      }
    );
    capture.noteEditModeSubmitted();
    setIsEditing(false);
  };

  const handleEditContainerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      handleCancelEdit();
    }
  };

  const handleSelectProfile = (
    profile: Profile,
    entityExtractionId: string | undefined
  ) => {
    if (!entityExtractionId || !profile.full_name || !fullNote) return;
    const entity = (fullNote.entity_extractions || []).find(
      (e) => e.id === entityExtractionId
    );
    if (
      !entity ||
      entity.start_position === undefined ||
      entity.end_position === undefined
    ) {
      return;
    }

    const currentText =
      fullNote.edited_text_original ?? fullNote.edited_text ?? sourceBody;
    const before = currentText.slice(0, entity.start_position);
    const after = currentText.slice(entity.end_position);
    const newText = `${before}${profile.full_name}${after}`;

    if (newText === currentText && entity.profile?.id === profile.id) return;

    updateShiftNote(
      {
        id: data.id,
        data: { edited_text: newText },
        clearEntities: true,
      },
      {
        onSuccess: () => {
          generateInstantReflex(data.id);
          extractEntities(data.id);
          capture.noteEditModeTaggedProfile();
        },
      }
    );
  };

  const mainMenuItems = [
    ...(isNoteOwner
      ? [
          {
            label: t('noteCardMenu.edit'),
            icon: <General.Edit05 size={20} />,
            onSelect: ({ close }: { close: () => void }) => {
              close();
              handleEnterEdit();
            },
          },
        ]
      : []),
    {
      label: t('noteCardMenu.share'),
      icon: <Communication.Send01 size={20} />,
      onSelect: () => {
        setMenuMode('share');
      },
    },
    {
      label: t('noteCardMenu.copy'),
      icon: <General.Copy03 size={20} />,
      onSelect: ({ close }: { close: () => void }) => {
        handleCopy();
        close();
      },
    },
    ...(isNoteOwner
      ? [
          {
            label: isPrivate
              ? t('noteCardMenu.markAsPublic')
              : t('noteCardMenu.markAsPrivate'),
            icon: renderPrivacyMenuIcon(),
            disabled: isTogglingPrivacy,
            onSelect: () => {
              if (isTogglingPrivacy) return;
              handleTogglePrivacy();
            },
          },
        ]
      : []),
    ...(isCameraNote
      ? [
          {
            label: t('noteCardMenu.markAsFalseAlarm'),
            icon: <General.XCircle size={20} />,
            onSelect: ({ close }: { close: () => void }) => {
              close();
              setShowFalseAlarmConfirm(true);
            },
          },
        ]
      : []),
    ...(isNoteOwner && canUnsyncFromEvent
      ? [
          {
            label: t('noteCardMenu.unsyncFromEvent'),
            icon: <General.LinkBroken01 size={20} />,
            onSelect: ({ close }: { close: () => void }) => {
              close();
              handleUnsyncFromEvent();
            },
          },
        ]
      : []),
    ...(isNoteOwner
      ? [
          {
            label: t('noteCardMenu.delete'),
            icon: <General.Trash01 size={20} />,
            destructive: true,
            onSelect: ({ close }: { close: () => void }) => {
              close();
              setShowDeleteConfirm(true);
            },
          },
        ]
      : []),
  ];

  const shareMenuItems = EMAIL_PROVIDERS.map((provider) => ({
    label: tShared(PROVIDER_LABEL_KEYS[provider]),
    icon: <Arrow.ArrowUpRight size={20} />,
    onSelect: ({ close }: { close: () => void }) => {
      handleShareToProvider(provider);
      close();
    },
  }));

  const menuItems = menuMode === 'share' ? shareMenuItems : mainMenuItems;
  const menuHeader =
    menuMode === 'share' ? t('noteCardMenu.shareVia') : undefined;

  return (
    <>
      <Styled.Card>
        <Styled.Header>
          <Styled.HeaderAuthor>
            <Styled.AuthorName>{authorName}</Styled.AuthorName>
            <Styled.HeaderTimeRow>
              <Styled.TimeLabel>
                {isEditing ? t('noteCardMenu.editing') : time}
              </Styled.TimeLabel>
              {originalBody && !isEditing && (
                <>
                  <Styled.MetaSeparator />
                  <NoteTranslationToggle
                    showingOriginal={showOriginal}
                    onToggle={() => setShowOriginal((current) => !current)}
                  />
                </>
              )}
            </Styled.HeaderTimeRow>
          </Styled.HeaderAuthor>
          <Styled.MenuWrapper
            onClick={stopClickPropagation}
            onKeyDown={stopClickPropagation}
          >
            <DropdownMenu
              isOpen={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              items={menuItems}
              headerLabel={menuHeader}
              usePortal
              renderTrigger={({ toggle, isOpen }) => (
                <Styled.MenuButton
                  type="button"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    toggle(e);
                  }}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  aria-label={t('noteCardMenu.openMenu')}
                >
                  <General.DotsHorizontal />
                </Styled.MenuButton>
              )}
            />
          </Styled.MenuWrapper>
        </Styled.Header>
        {isEditing ? (
          <>
            <Styled.Body
              as="div"
              onClick={stopClickPropagation}
              onKeyDown={handleEditContainerKeyDown}
              aria-label={t('noteCardMenu.editPlaceholder')}
            >
              <EditableNoteContent
                key={`editable-${data.id}-${
                  fullNote?.partial_extracted_at || 'no-extract'
                }-${(fullNote?.entity_extractions || [])
                  .map((e) => e.profile?.id ?? '')
                  .join(',')}`}
                text={editedText}
                entities={fullNote?.entity_extractions || []}
                profiles={profiles}
                entityProfileChanges={{}}
                onTextChange={setEditedText}
                onProfileChange={() => {}}
                onSave={handleSaveEdit}
                isEditing
                noteId={data.id}
                variant="compact"
              />
            </Styled.Body>
            <Styled.EditActionRow>
              <Buttons.SecondaryLightIcon
                $size="small"
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleCancelEdit();
                }}
                aria-label={t('noteCardMenu.deleteCancel')}
              >
                <General.XClose />
              </Buttons.SecondaryLightIcon>
              <Buttons.PrimaryIcon
                $size="small"
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleSaveEdit();
                }}
                disabled={!editedText.trim()}
                aria-label={t('noteCardMenu.saveNote')}
              >
                <General.Check />
              </Buttons.PrimaryIcon>
            </Styled.EditActionRow>
          </>
        ) : (
          <NoteCardBody
            text={displayedBody}
            entities={
              entitiesMatchDisplayedBody
                ? fullNote?.entity_extractions
                : undefined
            }
            profiles={profiles}
            noteId={data.id}
            onSelectProfile={handleSelectProfile}
          />
        )}
        {attachments.length > 0 && (
          <NoteAttachments
            noteId={data.id}
            attachments={attachments}
            isFromCamera={isCameraNote}
            canDelete={isNoteOwner}
          />
        )}
        {showMissingInfo && instantReflex && (
          <NoteCardMissingInfoSection
            key={instantReflex.id}
            instantReflex={instantReflex}
            noteId={data.id}
            initiallyExpanded={wasJustCreatedThisSession}
            isSpecialist={isSpecialist}
          />
        )}
        {isSpecialist &&
          (showAnswers || showGuidance || showEmptyReflex) &&
          instantReflex && (
            <ReflexFeedbackSection
              key={instantReflex.id}
              instantReflex={instantReflex}
              noteId={data.id}
              noteInstantReflexes={data.instantReflexes}
            />
          )}
        {!isSpecialist && showAnswers && instantReflex && (
          <NoteCardAnswersSection
            instantReflex={instantReflex}
            noteId={data.id}
            initiallyExpanded={wasJustCreatedThisSession}
          />
        )}
        {!isSpecialist && showGuidance && instantReflex && (
          <NoteCardGuidanceSection
            instantReflex={instantReflex}
            noteId={data.id}
            initiallyExpanded={wasJustCreatedThisSession}
          />
        )}
        {showLoading && <NoteCardLoadingSection phase={loadingPhase} />}
      </Styled.Card>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={t('noteCardMenu.deleteTitle')}
        contentText={t('noteCardMenu.deleteMessage')}
        cancelLabel={t('noteCardMenu.deleteCancel')}
        successLabel={t('noteCardMenu.deleteConfirm')}
        successCallback={handleDeleteConfirmed}
        handleClose={() => setShowDeleteConfirm(false)}
      />
      <ConfirmDialog
        isOpen={showEmergencyPrivacyConfirm}
        title={t('noteCardMenu.markPrivateEmergencyTitle')}
        contentText={t('noteCardMenu.markPrivateEmergencyMessage')}
        cancelLabel={t('noteCardMenu.deleteCancel')}
        successLabel={t('noteCardMenu.markPrivateEmergencyConfirm')}
        successCallback={handleConfirmEmergencyPrivacy}
        handleClose={handleCancelEmergencyPrivacy}
      />
      <ConfirmDialog
        isOpen={showFalseAlarmConfirm}
        title={t('noteCardMenu.markFalseAlarmTitle')}
        contentText={t('noteCardMenu.markFalseAlarmMessage')}
        cancelLabel={t('noteCardMenu.deleteCancel')}
        successLabel={t('noteCardMenu.markFalseAlarmConfirm')}
        successCallback={handleFalseAlarmConfirmed}
        handleClose={() => setShowFalseAlarmConfirm(false)}
      />
    </>
  );
}
